import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  CtAttributesService,
  TMetadataByScenarioAndAttributeName,
} from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { DashboardManagerService } from '@common-modules/right-panel/dashboards/dashboard-manager.service';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { DataTypeStr } from '@common-modules/shared/core/data-type-str';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { FiltrableItemTypeEnum } from '@common-modules/shared/model/filtrable-items/types/filtrable-item-type-enum';
import { forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { ctAllowedDataTypesByWidget } from '../ct-dashboard-mappings';
import { DashboardWidgetTypes } from '../models/dashboard-widget-types';
import { SearchResultAttribute } from '../models/search-result-attribute';
import { SearchResultItem } from './../models/search-result-item';
import { DashboardCartFiltrableItemService } from './dashboard-cart-filtrable-item.service';
import { DashboardCartItem } from './dashboard-cart-item';

const COMPONENT_SELECTOR = 'ct-create-dashboard-widget-cart';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './create-dashboard-widget-cart.component.html',
  styleUrl: './create-dashboard-widget-cart.component.scss',
  standalone: false,
  providers: [DashboardCartFiltrableItemService],
})
export class CreateDashboardWidgetCartComponent {
  @Input() widgetType: DashboardWidgetTypes;
  @Input() disableAddToDashboard = false;

  private _existingNetworkElements: string[] = [];
  @Input()
  set existingNetworkElements(value: string[]) {
    this._existingNetworkElements = value || [];
    if (value) {
      this.networkElements = this._existingNetworkElements;
    }
  }
  get existingNetworkElements(): string[] {
    return this._existingNetworkElements;
  }

  @Output() isValidCart = new EventEmitter<boolean>();
  @Output() cartSelectionSubmit = new EventEmitter<DashboardCartAndSettings>();
  @Output() onNetworkElementsChange = new EventEmitter<string[]>();

  cartItems: DashboardCartItem[] = [];
  currentTimeStep: Date;
  initialCartAndSettings: DashboardCartAndSettings;
  isValid = false;
  isSettingsComponent = false;
  networkElements: string[] = [];

  readonly T_SCOPE = `${AppModules.RightPanel}.${COMPONENT_SELECTOR}`;
  private readonly _ctAttributesService = inject(CtAttributesService);
  private readonly _dialogService = inject(DialogService);
  private readonly _dashboardManagerService = inject(DashboardManagerService);
  private readonly _cartFiltrableItemService = inject(DashboardCartFiltrableItemService);
  private readonly _measurementAllowedTypes = [DataTypeStr.Int, DataTypeStr.Double];

  addAttributes(attributes: SearchResultAttribute[]): void {
    const newCartItems = attributes.map((attribute) => {
      const cartItem: DashboardCartItem = {
        id: this.getAttributeCartItemId(attribute),
        title$: of(attribute.cartTitle),
        subtitle$: of(attribute.attributeName),
        filtrableType: FiltrableItemTypeEnum.CTResult,
        params: {
          ...attribute.params,
          attributeId: attribute.attributeId,
          units: attribute.units,
          physType: attribute.physType,
        },
        isVisible: true,
      };
      return cartItem;
    });

    this.cartItems = this.cartItems.concat(newCartItems);
    this.networkElements.push(...attributes.map((attr) => attr.cartTitle));
    this.onNetworkElementsChange.emit(this.networkElements);
    this.checkIsValid();

    this.buildCartAndSettings().subscribe((cartAndSettings) => {
      if (this.isSettingsComponent) {
        this.cartSelectionSubmit.emit(cartAndSettings);
        this._dashboardManagerService.updatedcardAndSettingsInSettingsComponent(cartAndSettings);
      }
    });
  }

  addMeasurement(measurement: SearchResultItem): void {
    const cartItem: DashboardCartItem = {
      id: measurement.resultId,
      title$: measurement.label$,
      subtitle$: measurement.breadcrumbs$,
      filtrableType: FiltrableItemTypeEnum.CTMeasurement,
      params: measurement.params,
    };

    this.cartItems.push(cartItem);
    this.checkIsValid();
  }

  initFromModel(cartAndSettings: DashboardCartAndSettings): void {
    this.initialCartAndSettings = cartAndSettings;
    this.widgetType = cartAndSettings.widgetType;
    this.currentTimeStep = cartAndSettings.timeStep;
    this._cartFiltrableItemService.buildCartItems(cartAndSettings).subscribe((cartItems) => {
      this.cartItems = cartItems;
      this.checkIsValid();
    });
  }

  setCurrentTimeStep(currentTimeStep: Date): void {
    this.currentTimeStep = currentTimeStep;
  }

  onRemoveCartItem(cartItemToRemove: DashboardCartItem): void {
    this.cartItems = this.cartItems.filter((cartItem) => cartItem.id !== cartItemToRemove.id);
    this.buildCartAndSettings().subscribe((cartAndSettings) => {
      if (this.isSettingsComponent) {
        this.cartSelectionSubmit.emit(cartAndSettings);

        this._dashboardManagerService.updatedcardAndSettingsInSettingsComponent(cartAndSettings);
      }
    });

    cartItemToRemove.title$.pipe(take(1)).subscribe((title) => {
      this.networkElements = this.networkElements.filter((e) => e !== title);
    });
    this.onNetworkElementsChange.emit(this.networkElements);

    this.checkIsValid();
  }

  onCartItemVisibilityChange(cartItem: DashboardCartItem): void {
    const idx = this.cartItems.findIndex((item) => item.id === cartItem.id);
    this.cartItems[idx].isVisible = !this.cartItems[idx].isVisible;

    this.buildCartAndSettings().subscribe((cartAndSettings) => {
      this.cartSelectionSubmit.emit(cartAndSettings);
      this._dashboardManagerService.updatedcardAndSettingsInSettingsComponent(cartAndSettings);
    });
  }

  onAddToDashboard(): void {
    this.buildCartAndSettings().subscribe((cartAndSettings) => {
      this.cartSelectionSubmit.emit(cartAndSettings);
    });
  }

  onApplySettingsComponentConfiguration(): void {
    this.isSettingsComponent = true;
  }

  checkIsValid(): void {
    this.isValid = !!this.cartItems.length;
    this.isValidCart.emit(this.isValid);
  }

  buildCartAndSettings(): Observable<DashboardCartAndSettings> {
    if (!this.cartItems || this.cartItems.length === 0) {
      return this.buildEmptyCartAndSettings();
    }
    return this.checkInconsistencies().pipe(
      switchMap(() =>
        this._cartFiltrableItemService.buildDashboardCartAndSettings(
          this.cartItems,
          this.widgetType,
          this.currentTimeStep,
          this.initialCartAndSettings?.dashboardChildId
        )
      ),
      map((cartAndSettings) => {
        const finalCartAndSettings = this.mergeWithInitial(cartAndSettings);
        return finalCartAndSettings;
      })
    );
  }

  buildEmptyCartAndSettings(): Observable<DashboardCartAndSettings> {
    return of(
      new DashboardCartAndSettings({
        dashboardChildId: this.initialCartAndSettings?.dashboardChildId ?? undefined,
        items: [],
        visibleItems: [],
        timeStep: this.currentTimeStep,
        widgetType: this.widgetType,
      })
    );
  }

  private getAttributeCartItemId(attribute: SearchResultAttribute): string {
    const id = this._cartFiltrableItemService.getAttributeCartItemId(
      attribute.attributeId,
      attribute.params.element.networkElementId
    );
    return id;
  }

  private mergeWithInitial = (cartAndSettings: DashboardCartAndSettings) => {
    return { ...(this.initialCartAndSettings || {}), ...cartAndSettings };
  };

  /**
   * Is different from isValid because it only shows a popup if an inconsistency is found, does not stop the flow.
   */
  private checkInconsistencies(): Observable<void> {
    let validations = null;

    if (!this.cartItems.length) {
      return of(validations);
    }

    const allowedDataTypes = ctAllowedDataTypesByWidget.get(this.widgetType) || [];

    return forkJoin([this._ctAttributesService.metadataByAttributeName$.pipe(take(1))]).pipe(
      map(([metadataByAttributeName]) => {
        for (const cartItem of this.cartItems) {
          let dataType = '';
          if (cartItem.filtrableType === FiltrableItemTypeEnum.CTResult) {
            dataType = this.getDataTypeFromAttribute(
              cartItem.params.attributeId,
              metadataByAttributeName
            );
          } else if (cartItem.filtrableType === FiltrableItemTypeEnum.CTMeasurement) {
            dataType = cartItem.params.measurement.attributeType;
          }

          if (this.widgetType === DashboardWidgetTypes.Text) {
            return null;
          }

          if (
            dataType &&
            !allowedDataTypes.find((allowedDataType) => allowedDataType === dataType)
          ) {
            this.showIncompatibleTypesPopup(cartItem);
            return null;
          }
        }
      })
    );
  }

  private showIncompatibleTypesPopup(cartItem: DashboardCartItem): void {
    console.warn(`Widget ${this.widgetType} does not accept the item ${cartItem.id}`);
    this._dialogService.showTranslatedMessage({
      translateKey: `${this.T_SCOPE}.messages.incompatible-types`,
      icon: 'warning',
    });
  }

  private getDataTypeFromAttribute(
    attributeName: string,
    metadataByScenarioAndAttribute: TMetadataByScenarioAndAttributeName
  ): string | null {
    for (let metadataByAttribute of metadataByScenarioAndAttribute.values()) {
      const metadata = metadataByAttribute.get(attributeName);
      if (metadata) {
        return metadata.attributeTypeKey;
      }
    }
    return null;
  }

  // private checkIsVisibleCart(
  //   cartItems: DashboardCartItem[],
  //   visibleItems: IFiltrableItemDto[]
  // ): DashboardCartItem[] {
  //   const visibleKeys = new Set((visibleItems || []).map((item) => item.filtrableType?.key));
  //   return cartItems.map((item) => ({
  //     ...item,
  //     isVisible: visibleKeys.has(item.id),
  //   }));
  // }
}
